<template>
    <Head title="Reflections Admin" />
    <BaseLayout>
        <BasePageHeader title="Reflections Admin" subtitle="Organization-wide campaign insights and reporting.">
            <template #eyebrow>
                <a href="/reflections" class="body-small text-valence-grey-700 hover:text-valence-grey-850 flex items-center gap-1">
                    <i class="bi bi-chevron-left" aria-hidden="true"></i> Reflections
                </a>
            </template>
        </BasePageHeader>

        <div v-if="loading" class="body-small text-valence-grey-750">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <section v-else class="flex flex-col gap-3">
            <div class="flex items-center justify-between">
                <h2 class="label-section">Campaigns</h2>
                <BaseButton theme="primary" size="sm" :disabled="templates.length === 0" @click="openCreateDialog">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i> New campaign
                </BaseButton>
            </div>

            <div v-if="campaigns.length === 0" class="rounded-xl border border-dashed border-valence-grey-200 bg-white px-5 py-6 body-small text-valence-grey-750">
                No campaigns yet. Create one to roll out an assessment to everyone in your organization.
            </div>

            <div v-for="campaign in campaigns" :key="campaign.id" class="relative">
                <BaseCard padding="md" as="a" :href="`/reflections/admin/campaigns/${campaign.id}/results`" class="flex items-center justify-between gap-4">
                    <div class="flex-1 min-w-0">
                        <div class="label-section" :class="stateColor(campaign.state)">
                            {{ campaign.state }}
                        </div>
                        <BaseHeader size="s" class="text-valence-grey-850 truncate mt-1">{{ campaign.name }}</BaseHeader>
                        <div class="body-small text-valence-grey-750 mt-1">
                            {{ campaign.template.name }} · {{ campaign.is_self_assessment_only ? "Self-assessment only · " : "" }}{{ campaign.cycle_count }} participants ·
                            {{ campaign.submitted_response_count }} responses
                        </div>
                        <div v-if="campaign.closes_at" class="details text-valence-grey-600 mt-1">Closes {{ formatDate(campaign.closes_at) }}</div>
                        <div v-if="campaign.has_report" class="details text-valence-grey-600 mt-1">
                            Report generated {{ formatDate(campaign.report_generated_at!) }}
                            <span v-if="campaign.new_submissions_since_report > 0" class="text-valence-gold-600 font-medium">
                                · {{ campaign.new_submissions_since_report }} new since last report
                            </span>
                        </div>
                        <div v-else class="details text-valence-grey-600 mt-1">No report generated yet</div>
                    </div>
                    <div class="flex items-center gap-3 shrink-0">
                        <StatusChip :state="stateChipState(campaign.state)">{{ campaign.state }}</StatusChip>
                        <i class="bi bi-arrow-right text-valence-grey-700" aria-hidden="true"></i>
                    </div>
                </BaseCard>
                <div class="absolute top-3 right-3 z-10 flex items-center gap-2">
                    <button
                        v-if="campaign.state === 'draft'"
                        type="button"
                        class="px-2 py-1 rounded-md border border-valence-green-300 bg-valence-green-50 text-valence-green-800 details font-medium hover:bg-valence-green-100 disabled:opacity-50"
                        :disabled="transitioningId === campaign.id"
                        @click.stop.prevent="launchCampaign(campaign)">
                        <i class="bi bi-send" aria-hidden="true"></i>
                        {{ transitioningId === campaign.id ? "Launching…" : "Launch" }}
                    </button>
                    <button
                        v-if="campaign.state !== 'closed'"
                        type="button"
                        class="px-2 py-1 rounded-md border border-valence-grey-200 bg-white text-valence-grey-750 details font-medium hover:bg-valence-grey-50 disabled:opacity-50"
                        :disabled="transitioningId === campaign.id"
                        @click.stop.prevent="closeCampaign(campaign)">
                        <i class="bi bi-x-circle" aria-hidden="true"></i> Close
                    </button>
                </div>
            </div>
            <BaseNotification v-if="actionStatus" :theme="actionStatus.theme" :text="actionStatus.text" @dismiss="actionStatus = null" />
        </section>

        <section v-if="teamsEnabled && !loading" class="flex flex-col gap-3">
            <div class="flex items-center justify-between">
                <h2 class="label-section">Teams</h2>
                <BaseButton theme="primary" size="sm" @click="importOpen = true"> <i class="bi bi-file-earmark-arrow-up" aria-hidden="true"></i> Import team CSV </BaseButton>
            </div>

            <div v-if="teams.length === 0" class="rounded-xl border border-dashed border-valence-grey-200 bg-white px-5 py-6 body-small text-valence-grey-750">
                No teams yet. Import a CSV of member emails and designate the team's lead — only the lead can see the team's results.
            </div>

            <BaseCard v-for="team in teams" :key="team.team_id" padding="md" class="flex items-center justify-between gap-4">
                <div class="min-w-0">
                    <div class="body-small-bold text-valence-grey-850 truncate">{{ team.name || "Unnamed team" }}</div>
                    <div class="body-small text-valence-grey-750 mt-0.5">
                        {{ team.lead_names.length ? `Lead: ${team.lead_names.join(", ")}` : "No lead assigned" }} · {{ team.member_count }}
                        {{ team.member_count === 1 ? "member" : "members" }}
                    </div>
                </div>
            </BaseCard>
        </section>

        <AssessmentDemoTools :campaign-actions="campaignDemoActions" />
    </BaseLayout>

    <TeamCsvImportDialog v-model:open="importOpen" @imported="reloadTeams" />

    <BaseDialog v-model:open="createOpen" title="New campaign" show-close>
        <form class="px-6 pb-6 flex flex-col gap-4 md:min-w-[480px]" @submit.prevent="submitCreate">
            <p class="body-semibold text-valence-grey-900">New campaign</p>
            <label for="campaign-name" class="block">
                <span class="block body-small-bold text-valence-grey-850 mb-2">Name</span>
                <input
                    id="campaign-name"
                    v-model="form.name"
                    type="text"
                    required
                    maxlength="255"
                    placeholder="e.g. Q3 2026 Leadership Assessment"
                    class="w-full px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small" />
                <span class="block details text-valence-grey-750 mt-1">Shown to participants on their reflections page.</span>
            </label>

            <fieldset>
                <legend class="block body-small-bold text-valence-grey-850 mb-2">Type</legend>
                <div class="flex flex-col gap-2">
                    <label for="campaign-type-360" class="flex items-start gap-3 cursor-pointer">
                        <input id="campaign-type-360" v-model="form.isSelfOnly" type="radio" name="campaign-type" :value="false" class="mt-1" />
                        <span>
                            <span class="block body-small-bold text-valence-grey-850">360 feedback</span>
                            <span class="block body-small text-valence-grey-750 mt-0.5">Participants nominate raters and collect anonymous feedback.</span>
                        </span>
                    </label>
                    <label for="campaign-type-self" class="flex items-start gap-3 cursor-pointer">
                        <input id="campaign-type-self" v-model="form.isSelfOnly" type="radio" name="campaign-type" :value="true" class="mt-1" />
                        <span>
                            <span class="block body-small-bold text-valence-grey-850">Self-assessment only</span>
                            <span class="block body-small text-valence-grey-750 mt-0.5">No raters — participants rate themselves and get their results right away.</span>
                        </span>
                    </label>
                </div>
            </fieldset>

            <label for="campaign-template" class="block">
                <span class="block body-small-bold text-valence-grey-850 mb-2">Template</span>
                <select
                    id="campaign-template"
                    v-model="form.templateId"
                    required
                    class="w-full px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small">
                    <option v-for="templateOption in eligibleTemplates" :key="templateOption.id" :value="templateOption.id">{{ templateOption.name }}</option>
                </select>
                <span v-if="eligibleTemplates.length === 0" class="block details text-red-600 mt-1">
                    No templates support this campaign type — pick a different type or add a template.
                </span>
                <span v-if="selectedTemplate" class="block details text-valence-grey-750 mt-1">
                    {{ selectedTemplate.question_count }} question{{ selectedTemplate.question_count === 1 ? "" : "s" }}
                </span>
            </label>

            <label v-if="!form.isSelfOnly" for="campaign-min-responses" class="block">
                <span class="block body-small-bold text-valence-grey-850 mb-2">Minimum rater responses</span>
                <input
                    id="campaign-min-responses"
                    v-model.number="form.minResponses"
                    type="number"
                    min="0"
                    max="32767"
                    required
                    class="w-32 px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small" />
                <span class="block details text-valence-grey-750 mt-1">Each participant's results unlock after this many raters respond. 0 unlocks immediately.</span>
            </label>

            <label v-if="!form.isSelfOnly" for="campaign-self-assess" class="flex items-start gap-3 cursor-pointer">
                <input id="campaign-self-assess" v-model="form.selfAssessEnabled" type="checkbox" class="mt-1" />
                <span>
                    <span class="block body-small-bold text-valence-grey-850">Include self-assessment</span>
                    <span class="block body-small text-valence-grey-750 mt-0.5">Participants also rate themselves, so their report shows self-vs-others gaps.</span>
                </span>
            </label>

            <label for="campaign-closes-at" class="block">
                <span class="block body-small-bold text-valence-grey-850 mb-2">Close date <span class="text-valence-grey-750 font-normal">(optional)</span></span>
                <input
                    id="campaign-closes-at"
                    v-model="form.closesOn"
                    type="date"
                    :min="todayIso"
                    class="px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small" />
                <span class="block details text-valence-grey-750 mt-1">Campaign hard-closes at the end of this day. Leave blank for no deadline.</span>
            </label>

            <BaseNotification v-if="createError" theme="error" :text="createError" hide-dismiss />

            <div class="flex items-center justify-end gap-3">
                <BaseButton theme="neutral" size="sm" :disabled="creating" @click="createOpen = false">Cancel</BaseButton>
                <BaseButton theme="primary" size="sm" :disabled="creating || !canSubmit" @click="submitCreate">{{ creating ? "Creating…" : "Create campaign" }}</BaseButton>
            </div>
            <p class="details text-valence-grey-600 text-right">Campaigns start as drafts — participants only see them once you launch.</p>
        </form>
    </BaseDialog>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { AdminCampaignResponse, AdminCampaignSummary, AdminHubPayload, TemplateSummary } from "~vue/api/generated/reflections";
import type { TeamOverview, TeamsOverviewResponse } from "~vue/api/generated/teams-csv";
import TeamCsvImportDialog from "~vue/AssessmentAdmin/TeamCsvImportDialog.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseHeader from "~vue/components/BaseHeader.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import { formatDate } from "~vue/utils/threeSixty";
import { computed, onMounted, ref, watch } from "vue";

const campaigns = ref<AdminCampaignSummary[]>([]);
const templates = ref<TemplateSummary[]>([]);
const loading = ref(true);
const loadError = ref<string | null>(null);
const transitioningId = ref<string | null>(null);
const actionStatus = ref<{ theme: "success" | "error"; text: string } | null>(null);

const teams = ref<TeamOverview[]>([]);
const teamsEnabled = ref(false);
const importOpen = ref(false);

const createOpen = ref(false);
const creating = ref(false);
const createError = ref<string | null>(null);
const form = ref({
    name: "",
    isSelfOnly: false,
    templateId: null as string | null,
    minResponses: 0,
    selfAssessEnabled: true,
    closesOn: "",
});

const todayIso = new Date().toISOString().slice(0, 10);
// Templates usable for the chosen campaign type: self-only campaigns can't
// use 360-only templates and vice versa (mirrors the backend validation).
const eligibleTemplates = computed<TemplateSummary[]>(() =>
    templates.value.filter((t) => (form.value.isSelfOnly ? t.assessment_mode !== "rater_360" : t.assessment_mode !== "self_only")),
);
const selectedTemplate = computed<TemplateSummary | null>(() => eligibleTemplates.value.find((t) => t.id === form.value.templateId) ?? null);
const canSubmit = computed(() => form.value.name.trim().length > 0 && selectedTemplate.value !== null && form.value.minResponses >= 0);
const campaignDemoActions = computed(() => campaigns.value.filter((campaign) => campaign.state !== "closed").map((campaign) => ({ id: campaign.id, label: campaign.name })));

// Keep the picker valid when the campaign type changes.
watch(eligibleTemplates, (eligible) => {
    if (!eligible.some((t) => t.id === form.value.templateId)) {
        form.value.templateId = eligible[0]?.id ?? null;
    }
});

// Snapshot the template's default minimum whenever the picker changes.
watch(selectedTemplate, (template) => {
    if (template) {
        form.value.minResponses = template.min_responses;
    }
});

function stateColor(state: string): string {
    switch (state) {
        case "active":
            return "text-valence-green-600";
        case "draft":
            return "text-valence-grey-600";
        case "closed":
            return "text-valence-grey-500";
        default:
            return "text-valence-grey-600";
    }
}

function stateChipState(state: string) {
    switch (state) {
        case "active":
            return "active";
        case "draft":
            return "neutral";
        case "closed":
            return "neutral";
        default:
            return "neutral";
    }
}

// The teams surface 404s when team_assessment_visibility is off for the org;
// the section simply stays hidden in that case.
async function reloadTeams(): Promise<void> {
    const { data, error: fetchError } = await useFetch("/v1/teams").json<TeamsOverviewResponse>();
    if (fetchError.value || !data.value) {
        teamsEnabled.value = false;
        return;
    }
    teamsEnabled.value = true;
    teams.value = data.value.teams;
}

onMounted(async () => {
    void reloadTeams();
    const { data, error: fetchError } = await useFetch("/v1/reflections/admin/campaigns").json<AdminHubPayload>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || "Could not load campaigns.";
        return;
    }
    campaigns.value = data.value?.campaigns ?? [];
    templates.value = data.value?.templates ?? [];
});

function openCreateDialog(): void {
    createError.value = null;
    const first = templates.value.find((t) => t.assessment_mode !== "self_only") ?? null;
    form.value = {
        name: "",
        isSelfOnly: false,
        templateId: first?.id ?? null,
        minResponses: first?.min_responses ?? 0,
        selfAssessEnabled: true,
        closesOn: "",
    };
    createOpen.value = true;
}

async function submitCreate(): Promise<void> {
    creating.value = true;
    createError.value = null;
    try {
        const { data, error: fetchError } = await useFetch("/v1/reflections/admin/campaigns")
            .post(
                JSON.stringify({
                    name: form.value.name.trim(),
                    template_id: form.value.templateId,
                    is_self_assessment_only: form.value.isSelfOnly,
                    min_responses: form.value.isSelfOnly ? 0 : form.value.minResponses,
                    self_assess_enabled: form.value.isSelfOnly ? true : form.value.selfAssessEnabled,
                    // End of the selected day, in the admin's local timezone.
                    closes_at: form.value.closesOn ? new Date(`${form.value.closesOn}T23:59:59`).toISOString() : null,
                }),
                "application/json",
            )
            .json<AdminCampaignResponse>();
        if (fetchError.value) {
            createError.value = (data.value as { error?: string } | null)?.error || "Could not create the campaign. Please try again.";
            return;
        }
        if (data.value) {
            campaigns.value = [data.value.campaign, ...campaigns.value];
        }
        createOpen.value = false;
        actionStatus.value = { theme: "success", text: `Created "${form.value.name.trim()}" as a draft. Launch it when you're ready.` };
    } finally {
        creating.value = false;
    }
}

async function transitionCampaign(campaign: AdminCampaignSummary, action: "launch" | "close"): Promise<void> {
    transitioningId.value = campaign.id;
    actionStatus.value = null;
    const { data, error: fetchError } = await useFetch(`/v1/reflections/admin/campaigns/${campaign.id}/${action}`, { method: "POST" }).json<AdminCampaignResponse>();
    transitioningId.value = null;
    if (fetchError.value) {
        actionStatus.value = { theme: "error", text: (data.value as { error?: string } | null)?.error || `Could not ${action} the campaign.` };
        return;
    }
    if (data.value) {
        campaigns.value = campaigns.value.map((existing) => (existing.id === campaign.id ? (data.value as AdminCampaignResponse).campaign : existing));
    }
    actionStatus.value = {
        theme: "success",
        text: action === "launch" ? `Launched "${campaign.name}" — it's now visible to everyone in your organization.` : `Closed "${campaign.name}".`,
    };
}

async function launchCampaign(campaign: AdminCampaignSummary): Promise<void> {
    if (!globalThis.confirm(`Launch "${campaign.name}"? It becomes visible to everyone in your organization.`)) {
        return;
    }
    await transitionCampaign(campaign, "launch");
}

async function closeCampaign(campaign: AdminCampaignSummary): Promise<void> {
    if (!globalThis.confirm(`Close "${campaign.name}"? This also closes all participant cycles and can't be undone.`)) {
        return;
    }
    await transitionCampaign(campaign, "close");
}
</script>
